import { Icon } from '@ps/isp-web-component-library';
import { Cash } from '@ps/isp-web-component-library/icons/Cash';
import { Gift } from '@ps/isp-web-component-library/icons/Gift';
import { Home } from '@ps/isp-web-component-library/icons/Home';
import { InPlay } from '@ps/isp-web-component-library/icons/InPlay';
import { MyBets } from '@ps/isp-web-component-library/icons/MyBets';
import { Settings } from '@ps/isp-web-component-library/icons/Settings';
import { Sites } from '@ps/platform-message-framework/common/Sites';
import { FeatureFlag } from '@ps/web-core-enums';
import { select, useDispatch, useFeature } from '@ps/web-core-store';
import { Translation } from '@ps/web-shared-components';
import { type IspSportsAlias } from '@ps/web-sports-config';
import { generateSportsPath } from '@ps/web-sports-utils';
import classNames from 'clsx';
import type React from 'react';
import { useSelector } from 'react-redux';

import styles from './SportsQuickLinksWidget.module.scss';
import HorizontalMenuItem from './components/HorizontalMenuItem/HorizontalMenuItem';
import SportsMenuToggleButton from './components/SportsMenuToggleButton/SportsMenuToggleButton';
import {
  TXTCLI_Promotions,
  TXTCLI_Settings,
  TXTCLI_Withdraw,
  TXTWCL_Home_7,
  TXTWCL_In_Play,
  TXTWCL_My_Bets
} from './translations';
import type { QuickLink } from './types';

const SportsQuickLinksWidget: React.FC = () => {
  const aliases = useSelector(select.cms.getAliases);
  const isSelfExcluded = useSelector(select.account.getSelfExclusion);
  const { modal } = useDispatch();
  const isPromotionsEnabled = useFeature(FeatureFlag.ISPPromotions);
  const isPromotionsQuickLinkEnabled = useFeature(FeatureFlag.ISPEnablePromotionsQuickLink);
  const isBannedByRegulator = useSelector(select.account.bannedByRegulator);
  const site = useSelector(select.cms.getSiteId);
  const isDeSite = site === Sites.DE;
  const isDeBannedByRegulator = isDeSite && isBannedByRegulator;

  const createUrl = (alias: IspSportsAlias, values = {}) =>
    aliases[alias] && generateSportsPath(aliases[alias] as string, values);

  const promotionsLink: QuickLink = {
    text: TXTCLI_Promotions,
    url: createUrl('sportsPromotions'),
    icon: <Icon icon={Gift} size="x2.5" />
  };

  const regularMenuItems: QuickLink[] = [
    {
      text: TXTWCL_Home_7,
      url: createUrl('sportsHome'),
      icon: <Icon icon={Home} size="x2.5" />,
      isActive: (_, { pathname }) => pathname === createUrl('sportsHome')
    },
    {
      text: TXTWCL_In_Play,
      url: createUrl('sportsInPlay'),
      icon: <Icon icon={InPlay} size="x2.5" />
    },
    {
      text: TXTWCL_My_Bets,
      url: createUrl('sportsMyBets', { status: 'open' }),
      icon: <Icon icon={MyBets} size="x2.5" />,
      isActive: (_, { pathname }) => {
        const url = createUrl('sportsMyBets');
        return !!url && pathname.startsWith(url);
      }
    },
    ...(isPromotionsEnabled && isPromotionsQuickLinkEnabled ? [promotionsLink] : []),
    {
      text: TXTCLI_Settings,
      url: createUrl('sportsSettings'),
      icon: <Icon icon={Settings} size="x2.5" />,
      className: classNames(styles.settings)
    }
  ];

  const selfExcludedMenuItems: QuickLink[] = [
    {
      text: TXTWCL_Home_7,
      url: createUrl('sportsSelfExcluded'),
      icon: <Icon icon={Home} size="x2.5" />,
      isActive: (_, { pathname }) => pathname === createUrl('sportsSelfExcluded')
    },
    {
      text: TXTCLI_Withdraw,
      onClick: () => modal.enqueue({ path: '/account/cashier/withdraw' }),
      icon: <Icon icon={Cash} size="x2.5" />
    },
    {
      text: TXTWCL_My_Bets,
      url: createUrl('sportsSelfExcludedMyBets', { status: 'open' }),
      icon: <Icon icon={MyBets} size="x2.5" />,
      isActive: (_, { pathname }) => {
        const url = createUrl('sportsSelfExcludedMyBets');
        return !!url && pathname.startsWith(url);
      }
    }
  ];

  return (
    <nav data-testid="sports-quick-links-widget" className={styles.quickLinks}>
      <ol className={styles.list}>
        {(isSelfExcluded || isDeBannedByRegulator ? selfExcludedMenuItems : regularMenuItems).map(
          ({ text, ...rest }, index) => (
            <HorizontalMenuItem key={index} text={<Translation {...text} />} {...rest} />
          )
        )}
        {!isSelfExcluded && !isDeBannedByRegulator ? <SportsMenuToggleButton /> : null}
      </ol>
    </nav>
  );
};

export default SportsQuickLinksWidget;
